'use client';

import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  IconCash,
  IconClock,
  IconHome2,
  IconInvoice,
  IconLockCog,
  IconSettings,
} from '@tabler/icons-react';
import { useSession } from 'next-auth/react';
import { AppShell, Burger, NavLink } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { UserButton } from '@/components/UserButton/UserButton';
import { useIsAuthorized } from '@/hooks/useIsAuthorized';
import { signInPagePath } from '@/utils/signInPolicy';
import { timeReportPermissionUsers } from '@/utils/timeReportPermissions';

const MyAppShell: FC<PropsWithChildren> = ({ children }) => {
  const { isAuthorized } = useIsAuthorized(timeReportPermissionUsers);
  const [opened, { toggle }] = useDisclosure();

  const showInvoiceEmails = ['timothy.trowbridge@collana.com', 'patrick.bundi@collana.com'];
  const email = useSession().data?.user?.email;

  const links = [
    {
      label: 'Project Rate',
      href: '/',
      icon: <IconCash size="1rem" stroke={1.5} />,
    },
    { label: 'Settings', href: '/settings', icon: <IconSettings size="1rem" stroke={1.5} /> },
    ...(isAuthorized
      ? [
          {
            label: 'Time Report Permissions',
            href: '/time-report-permissions',
            icon: <IconLockCog size="1rem" stroke={1.5} />,
          },
        ]
      : []),
    ...(email && showInvoiceEmails.includes(email)
      ? [
          {
            label: 'Invoice',
            href: '/invoice',
            icon: <IconInvoice size="1rem" stroke={1.5} />,
          },
        ]
      : []),
    {
      label: '[old] Time Reports',
      href: '/time-reports',
      icon: <IconClock size="1rem" stroke={1.5} />,
    },
  ];

  const pathname = usePathname();

  const [navOffset, setNavOffset] = useState(0);
  const navRef = useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery('(max-width: 48em)');

  useEffect(() => {
    if (isMobile) {
      setNavOffset(0);
    } else if (navRef.current) {
      setNavOffset(navRef.current.offsetWidth);
    }
  }, [isMobile]);

  // The sign-in page is rendered without a session, so it gets no navigation and no user menu.
  // Placed after the hooks above so they still run unconditionally on every route.
  if (pathname === signInPagePath) {
    return <>{children}</>;
  }

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 'fit-content',
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />

          <div style={{ marginLeft: 'auto', paddingRight: '10px' }}>
            <UserButton />
          </div>
        </div>
      </AppShell.Header>

      <AppShell.Navbar p="md" ref={navRef}>
        {links.map((link) => (
          <NavLink
            key={link.label}
            label={link.label}
            component={Link}
            href={link.href}
            color="orange"
            leftSection={link.icon}
            variant="filled"
            active={pathname === link.href}
          />
        ))}
      </AppShell.Navbar>

      <AppShell.Main
        style={{
          paddingLeft: `calc(${navOffset}px + var(--app-shell-padding))`,
        }}
      >
        {children}
      </AppShell.Main>
    </AppShell>
  );
};

export { MyAppShell };
